<template>
  <div class="d-flex flex-column flex-sm-row" style="gap: 16px">
    <div class="d-flex flex-column flex-grow-1" style="min-width: 0px">
      <!----------------------------- Category ----------------------------->
      <div class="mb-8" v-if="
        ((categoryId == null || categoryId == '') && serviceId == null) ||
        serviceId == ''
      ">
        <div class="text-h4 py-2">{{ $translate('Choose') }} {{ categoryTitle }}</div>
        <div v-if="noCategoriesFound" class="text-caption grey--text">
          {{ $translate('None available found') }}
        </div>
        <div v-else-if="categories.length === 0" class="text-caption grey--text">
          {{ $translate('None available found') }}
        </div>
        <v-btn-toggle borderless color="primary" v-else v-model="selectedCategory" class="d-flex flex-wrap" mandatory
          variant="flat">
          <v-btn v-for="category in categories" v-bind:key="category.id" :value="category" class="flex-grow-1">
            {{ category.name }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <!----------------------------- Service ----------------------------->
      <div class="mb-8" v-if="(serviceId == null || serviceId == '') && categories.length != 0">
        <div class="text-h4 py-2">{{ $translate('Choose') }} {{ serviceTitle }}</div>
        <div v-if="filteredServices.length === 0" class="text-caption grey--text">
          {{ $translate('None available found') }}
        </div>
        <v-btn-toggle borderless mandatory color="primary" v-else v-model="selectedService" class="d-flex flex-wrap">
          <v-btn v-for="service in filteredServices" v-bind:key="service.id" :value="service" class="flex-grow-1">
            {{ service.name }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <!----------------------------- Resources ----------------------------->
      <div v-show="resourceSlots.length != 0 && !!selectedCategory && resourceStyle !== 0" class="mb-8">
        <div class="text-h4 py-2">{{ $translate('Choose resource') }}</div>
        <div v-if="showTags && availableResourceTags.length" class="mb-2">
          <v-chip
            v-for="tag in availableResourceTags"
            :key="tag.id"
            :outlined="!selectedTagIds.includes(tag.id)"
            :close="selectedTagIds.includes(tag.id)"
            color="primary"
            label
            class="mr-2 mb-2"
            @click="toggleTagFilter(tag.id)"
            @click:close="toggleTagFilter(tag.id)"
          >
            {{ tag.name }}
          </v-chip>
        </div>
        <template v-if="resourceStyle === 1">
          <v-row>
            <v-col cols="4" sm="3" v-for="resource in resources" v-bind:key="resource.resourceId"
              class="d-flex align-stretch">
              <v-card class="rounded-lg flex-grow-1" gap="0">
                <div @click="toggleResource(resource.resourceId)" style="cursor: pointer" class="white">
                  <v-auto-img class="zoezi-staff-image rounded-lg pa-2 d-flex align-center" cover :src="resource.imagekey"
                    style="position: relative; aspect-ratio: 16/10">
                    <v-fade-transition>
                      <v-overlay v-show="selectedResourceIds.includes(resource.resourceId)" absolute color="white"
                        :style="{ zIndex: 1 }" />
                    </v-fade-transition>
                    <v-btn dark fab x-small :color="selectedResourceIds.includes(resource.resourceId)
                      ? 'primary'
                      : 'grey'
                      " :outlined="!selectedResourceIds.includes(resource.resourceId)
                        " class="top-right-btn" :style="{ zIndex: 2 }">
                      <v-icon v-show="selectedResourceIds.includes(resource.resourceId)" dark small>mdi-check</v-icon>
                    </v-btn>
                    <v-icon v-if="resource.imagekey == null || resource.imagekey == ''"
                      class="d-flex justify-center">mdi-image-off</v-icon>
                  </v-auto-img>
                </div>
                <v-card-title class="text-subtitle-1 font-weight-medium pt-2 pb-0 text-break resource-card-title">
                  {{ resource.title }}
                </v-card-title>
                <v-card-actions class="pt-0">
                  <v-btn text color="primary" @click="openDialog(resource.resourceId)">{{ $translate('Read more')
                  }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <template v-else-if="resourceStyle === 2">
          <div class="z-compact-resources d-flex flex-wrap">
            <v-chip
              v-for="resource in resources"
              :key="resource.resourceId"
              class="ma-1 z-compact-chip"
              label
              :outlined="!selectedResourceIds.includes(resource.resourceId)"
              :color="selectedResourceIds.includes(resource.resourceId) ? 'primary' : undefined"
              @click="toggleResource(resource.resourceId)"
            >
              <v-avatar left size="24">
                <v-auto-img :src="resource.imagekey">
                  <v-icon v-if="!resource.imagekey" small>mdi-image-off</v-icon>
                </v-auto-img>
              </v-avatar>
              <span class="z-compact-name text-truncate">{{ resource.title }}</span>
              <v-btn icon x-small class="ml-1" @click.stop="openDialog(resource.resourceId)">
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn>
            </v-chip>
          </div>
        </template>
      </div>
      <!----------------------------- Time ----------------------------->
      <div ref="top" class="mb-8">
        <div class="text-h4 py-2">{{ $translate('When do you want to book?') }}</div>
        <div class="d-flex align-center">
          <v-btn-toggle v-if="viewSetting === 'both' && weekViewAvailable" v-model="viewMode" mandatory class="mr-4">
            <v-btn value="day">{{ $translate('Day') }}</v-btn>
            <v-btn value="week">{{ $translate('Week') }}</v-btn>
          </v-btn-toggle>

          <template v-if="viewMode === 'day'">
            <v-btn icon small color="primary" @click="decrementDate" class="mr-2" :disabled="isDateToday">
              <v-icon dark small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-menu v-model="showDatePicker" :close-on-content-click="false" offset-y max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-on="on" v-bind="attrs" text py-1 class="mr-2 text-none black--text">
                  {{ selectedDate }}
                  <label v-show="selectedDate === Date.today().yyyymmdd()">
                    ({{ $translate('Today') }})</label>
                </v-btn>
              </template>
              <v-card max-width="300">
                <DatePicker
                  :value="selectedDate"
                  :min-date="$store.state.now30s.clone().clearTime().yyyymmdd()"
                  id="datepicker"
                  ref="datepicker"
                  @input="selectedDate = $event.yyyymmdd()"
                />
                <div class="mx-2 pt-2 pb-2">
                  <filter-button-row icon="mdi-calendar-today" :name="$translate('All day')" :value="[0, DAY_PERIODS.length].includes(selectedDayPeriods.length)
                    " />
                  <filter-button-row v-for="item in DAY_PERIODS" :key="item.id" :color="item.color"
                    :name="$translate(item.title)" :icon="item.icon" :value="selectedDayPeriods.includes(item.id)"
                    @input="setItem(selectedDayPeriods, item.id, $event)" />
                </div>
              </v-card>
            </v-menu>
            <v-btn icon small color="primary" @click="incrementDate">
              <v-icon dark small>mdi-chevron-right</v-icon>
            </v-btn>
          </template>

          <template v-else>
            <v-btn icon small color="primary" @click="decrementWeek" class="mr-2" :disabled="isWeekAtCurrent">
              <v-icon dark small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-menu v-model="showWeekPicker" :close-on-content-click="false" offset-y max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-on="on" v-bind="attrs" text py-1 class="mr-2 text-none black--text">{{ weekRangeLabel }}</v-btn>
              </template>
              <v-card max-width="300">
                <DatePicker
                  :value="selectedWeekStart"
                  :min-date="$store.state.now30s.clone().clearTime().yyyymmdd()"
                  @input="onWeekPicked"
                />
              </v-card>
            </v-menu>
            <v-btn icon small color="primary" @click="incrementWeek">
              <v-icon dark small>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </div>
        <v-divider />

        <!-- Only the week view needs an explicit group: its cells collapse every
             resource into one per day+time. The day view is a per-resource list and
             shows all resources' times without a selection. -->
        <div v-if="viewMode === 'week' && !selectedResourceIds.length"
          class="ma-6 d-flex align-center justify-center text-center">
          <v-icon class="mr-2">mdi-account-alert-outline</v-icon>{{ $translate('Select a resource to see available times') }}
        </div>

        <WeekOverviewGrid
          v-else-if="viewMode === 'week'"
          :days="weekDaysForGrid"
          :time-rows="weekTimeRowsForGrid"
          :cell-states="weekCellStates"
          :selected-days="selectedDaysList"
          :loading="isWeekLoading"
          :disabled="isReserving || bulkSelecting"
          @book-slot="onBookSlot"
          @toggle-day="onToggleDay"
        />

        <div v-else-if="filteredResources.length == 0 || filteredSlots.length == 0"
          class="ma-6 d-flex align-center justify-center">
          <v-icon class="mr-2">mdi-calendar-alert-outline</v-icon>{{ $translate('No available times found') }}
        </div>
        <div v-else class="d-flex flex-column flex-sm-row slots-container">
          <div class="d-flex flex-row flex-sm-column mt-0 mt-sm-9 flex-grow-0">
            <div v-for="resource in filteredResources" v-bind:key="resource.resourceId"
              class="d-flex align-center flex-column flex-sm-row resource-slot mt-2 mt-sm-0 mb-2 mr-2">
              <v-auto-img
                class="zoezi-staff-image elevation-0 rounded-lg mr-sm-4 d-flex align-center white flex-grow-0 resource-image-small"
                cover aspect-ratio="16/10" :src="resource.imagekey" style="position: relative">
                <v-icon v-if="resource.imagekey == null || resource.imagekey == ''"
                  class="d-flex justify-center">mdi-image-off</v-icon>
              </v-auto-img>
              <div class="d-flex justify-space-between flex-grow-1" style="height: 52px">
                <div class="text-subtitle-1 font-weight-medium text-wrap flex-grow-0 overflow-hidden resource-title">
                  {{ resource.title }}
                </div>
                <v-btn icon small color="primary" @click="openDialog(resource.id)" class="">
                  <v-icon dark small>mdi-information-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div class="slot-grid d-flex flex-row flex-sm-column align-start justify-start" style="width: 100%">
            <div v-if="Array.from(uniqueStarts).length > 0 && !compactDay"
              class="slot-row flex-shrink-1 d-flex flex-column flex-sm-row">
              <div class="timeslots select-all-slots">
                <div v-for="start in Array.from(uniqueStarts).sort()" :key="start" style="width: 100px"
                  class="d-flex align-center justify-center">
                  <v-btn
                    text
                    elevation="0"
                    plain
                    small
                    :disabled="isReserving"
                    @click="handleColumnClick(start)"
                  >
                    {{ getColumnButtonText(start) }}
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-for="resource in filteredResources" v-bind:key="resource.resourceId"
              class="slot-row d-flex flex-column flex-sm-row">
              <div v-for="start in startsForResource(resource)" :key="start" class="timeslot" :loading="reserving">
                <v-btn :key="start" x-large block v-if="hasSlotForStart(resource, start)"
                  @click="handleSlotClick(resource, start)" :color="getSlotColor(resource, start)"
                  :disabled="isReserving" depressed>
                  {{ Date.newFull(start).hhmm() }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!----------------------------- Resourcedialog ----------------------------->
      <StaffInfoDialog :value="isDialogShown" :staffId="dialogStaffId" @input="isDialogShown = $event"/>
      <!----------------------------- Checkout  ----------------------------->
      <bottom-dialog v-model="showCheckout" scrollable :max-width="almostdone ? '400px' : '850px'" :dialog="!$vuetify.breakpoint.mobile"
        :persistent="!almostdone" v-if="showCheckout">
        <v-card class="zoezi-bottom-sheet" tile elevation="0" style="position: relative">
          <v-btn v-if="almostdone" icon small style="position: absolute; top: 8px; right: 8px; z-index: 1" @click="checkoutDone()">
            <v-icon color="primarytext">mdi-close</v-icon>
          </v-btn>
          <v-card-title v-if="!almostdone" class="headline align-start flex-column pa-2 primary primarytext--text">
            <div class="font-weight-bold fill-width">
              <span>{{
                $translate("Complete booking")
              }}</span>
              <v-btn icon @click="showCheckout = false" class="float-right" v-if="!checkoutLoading"><v-icon
                  class="primary primarytext--text">mdi-close</v-icon></v-btn>
            </div>
          </v-card-title>

          <v-card-text :class="{
            'px-2': !almostdone && $vuetify.breakpoint.mobile,
            'px-0': almostdone,
            'pb-0': almostdone,
            'pt-0': almostdone,
            primary: almostdone,
          }" class="z-no-x-scroll" :style="almostdone ? {} : {'min-height': '50vh'}">
            <checkout ref="checkout" class="overflow-y-auto" :items="checkoutItems" expand-first-item
              :show-summary="true" :show-back-link="false" :show-payment-button="false" :show-sum="showPrice"
              :force-done="almostdone" @done="checkoutDone" @almostdone="almostdone = true"
              @loading="checkoutLoading = $event" @cost="cost = $event" @readyToPay="readyToPay = $event"
              @readyToPayExceptPaymentMethod="
                readyToPayExceptPaymentMethod = $event
                " @changeNumberOfItems="changeNumberOfItems" @changeItemUsers="changeItemUsers" content-target="checkoutcontent" :dialog="true"
              @reservation-timeout="reservationTimeout" :force-loading="checkoutLoading">
              <template #done="{ context: selectedSlot }">
                <div class="d-flex justify-center" style="width: 100%">
                <div class="zoezi-resourcebooking-done d-flex flex-column align-center pa-4" style="max-width: 380px; width: 100%">
                  <div class="zoezi-resourcebooking-done-header-row mb-3 d-flex justify-center primarytext--text">
                    <i class="done-icon" :class="{ active: animateDone }" style="font-size: 42px"></i>
                  </div>

                  <v-card class="zoezi-resourcebooking-done-card" style="width: 100%">
                    <v-card-text class="pa-3">
                      <div class="zoezi-resourcebooking-done-welcome primary--text font-weight-bold mb-1">
                        {{ $translate("Warm welcome!") }}
                      </div>
                      <div class="font-weight-bold mb-2">
                        {{ selectedSlot.service.name }}
                      </div>
                      <div>
                        <v-icon small class="mr-1">mdi-calendar-month-outline</v-icon>{{ formatTime(selectedSlot.start, selectedSlot.period) }}
                      </div>
                      <div>
                        <v-icon small class="mr-1">mdi-clock-outline</v-icon>{{ $translate("At {0} o'clock").format(selectedSlot.start.hhmm()) }}
                      </div>
                      <div>
                        <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>{{ contactinfo.visit_address }}, {{ contactinfo.visit_zipCode }} {{ contactinfo.visit_city }}
                      </div>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions class="px-3 py-2 d-flex align-center">
                      <v-avatar size="36" class="mr-2">
                        <v-auto-img class="zoezi-staff-image" :src="selectedSlot.staff.imagekey" />
                      </v-avatar>
                      <span class="text-body-2">&ndash;{{ selectedSlot.staff.name }}</span>
                      <v-spacer />
                      <v-btn text small color="primary" class="text-body-2 text-no-wrap px-0" @click="openGoogle()">{{ $translate("Show on map") }}</v-btn>
                      <v-icon small color="primary" class="ml-1">mdi-map-marker-outline</v-icon>
                    </v-card-actions>
                  </v-card>

                  <v-btn v-if="numBookings" @click="gotoMyPages" color="primary" fab bottom right fixed :style="{
                    bottom: $vuetify.breakpoint.mobile ? 'calc(56px + 16px)' : '16px',
                  }">
                    <v-badge :key="numBookings" :content="numBookings" color="red" :value="numBookings"
                      class="animate__animated animate__heartBeat" left>
                      <v-icon color="primarytext">mdi-ticket</v-icon>
                    </v-badge>
                  </v-btn>
                </div>
                </div>
              </template>

              <template #content>
                <stepper-content v-model="currentStep" :steps="steps">
                  <template #questions>
                    <v-form @submit.prevent="
                      allQuestionsDone && $refs.stepper.nextClicked()
                      ">
                      <v-expansion-panels v-model="expandedQuestionPanels" accordion flat multiple>
                        <v-expansion-panel
                          v-for="(reservation, index) in reservationsWithQuestions"
                          :key="index"
                        >
                          <expansion-panel-header
                            icon="mdi-comment-question-outline"
                            :title="getQuestionPanelTitle(reservation)"
                            :subtitle="$translate('Answer the questions to proceed')"
                          />
                          <v-expansion-panel-content class="z-dense">                  
                            <form-field
                              v-for="q in reservation.questions"
                              :key="q.name"
                              :field="q"
                              star
                              v-model="q.value"
                            />
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-form>
                  </template>

                  <template #optional_resources>
                    <OptionalResources class="OptionalResources" :disabled="isReserving"
                      :resource-name="steps[currentStep].context.resource_name"
                      :resource-ids="steps[currentStep].context.resource_ids"
                      :start-time="steps[currentStep].context.start" :end-time="steps[currentStep].context.end"
                      :all-resources="steps[currentStep].context.service.resources" 
                      :selected-resource-ids="steps[currentStep].context.selected_resource_ids"
                      :bookings="optionalCtx.groupBookings"
                      :pending-selections="optionalCtx.pending_selections"
                      @select="
                          (resource_id, selected) =>
                            optionalResourceSelectedGroup(
                              steps[currentStep].context.reservation,
                              steps[currentStep].context.groupBookings,
                              resource_id,
                              selected
                            )
                        " />
                  </template>

                  <template #extra_person>
                    <div class="mb-2 font-weight-bold">
                      {{
                        $translate(
                          "You have the opportunity to schedule more at the same time and resource. Do you want to do that? "
                      )
                      }}
                    </div>

                    <div>
                      <v-icon>mdi-account-question-outline</v-icon><span class="ml-2">{{
                        $translate("Who to book?")
                      }}</span>
                    </div>

                    <div class="z-rounded-box my-2">
                      <div class="pa-2 d-flex">
                        <div class="ml-2 flex-grow-1">
                          <span class="font-weight-bold" v-if="extraPersons.length == 0">{{
                            $store.state.user && $store.state.user.name
                            }}
                            ({{ $translate("You") }})</span>
                          <span class="font-weight-bold" v-else>{{
                            $translate("{0} people including you").format(
                              extraPersons.length + 1
                            )
                          }}</span>
                        </div>
                      </div>

                      <div class="px-2 py-1 d-flex align-center" v-for="(person, index) in extraPersons" :key="index">
                        <v-text-field v-model="person.name" :label="$translate('Name')" dense :rules="[
                          (v) =>
                            !!v ||
                            $translate('Write the name of the participant'),
                        ]" />
                        <v-btn class="ml-3" @click="removeExtraPerson(index)" icon>
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <div v-if="checkingAvailability" class="d-flex align-center">
                      <v-progress-circular indeterminate size="24"></v-progress-circular>
                      <span class="ml-2">{{ $translate("Updating...") }}</span>
                    </div>
                    <div>
                      {{ seatsLeftText }}
                    </div>

                    <v-btn color="primary" :outlined="true" block class="zoezi-button-thin" @click="addExtraPerson"
                      :disabled="!seatsLeft || isReserving || checkingAvailability
                        ">
                      <v-icon left color="primary">mdi-account-plus</v-icon>
                      {{ $translate("Add participant") }}
                    </v-btn>
                  </template>

                  <template #products>
                    <v-expansion-panels v-model="expandedProductPanel" accordion flat>
                      <v-expansion-panel class="mt-2" v-for="reservation in reservations.filter(
                        (r) => additionalProducts[r.key].length
                      )" :key="reservation.key">
                        <expansion-panel-header icon="mdi-tag-plus-outline" :title="$translate('Buy more for {0}').format(
                          services.find(
                            (s) =>
                              s.id == reservation.bookings[0].rbservice_id
                          ).name
                        )
                          " :subtitle="$translate('Buy the products you need')" />
                        <v-expansion-panel-content class="z-dense">
                          <additional-product-list :products="additionalProducts[reservation.key]"
                            :basket="reservation.basket" :total-basket="productsInBasket" :show-added-banner="false" />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </template>
                  <template #checkout>
                    <portal-target name="checkoutcontent" />
                  </template>
                </stepper-content>
              </template>
            </checkout>
          </v-card-text>
          <v-card-actions v-if="!almostdone">
            <stepper v-model="currentStep" :steps="steps" :fixed="$vuetify.breakpoint.mobile"
              @cancel="showCheckout = false" ref="stepper" :show="!almostdone" :disabled="checkoutLoading" />
          </v-card-actions>
        </v-card>
      </bottom-dialog>
    </div>

    <!----------------------------- Summary for reservation ----------------------------->
    <div class="d-flex flex-column flex-shrink-0 w-sm-100 checkout-summary">
      <div class="d-flex flex-column flex-grow-0 flex-shrink-0 checkout-card sticky-column">
        <v-card class="mb-4">
          <v-card-title> {{ $translate('Your Booking') }} </v-card-title>
          <v-divider class="mx-4"></v-divider>
          <v-card-title class="pt-4 pb-0">{{
            capitalizeFirstLetter(categoryTitle)
          }}</v-card-title>
          <v-card-text v-if="
            !selectedCategory &&
            globalSiteSetting &&
            !this.$store.state.selectedSiteId
          " class="text-caption grey--text pt-0">
            {{ $translate('Choose site') }}
          </v-card-text>
          <v-card-text v-else-if="!selectedCategory" class="text-caption grey--text pt-0">
            {{ $translate('NO_PRODUCT_CHOSEN') }}
          </v-card-text>
          <v-card-text v-else class="pt-0">{{
            selectedCategory ? selectedCategory.name : ""
          }}</v-card-text>
          <v-card-title class="pt-4 pb-0">{{ $translate('Reserved times') }}</v-card-title>
          <v-card-text>
            <div v-if="formattedShoppingCart.length === 0" class="text-caption grey--text">
              {{ $translate('NO_PRODUCT_CHOSEN') }}
            </div>
            <div v-for="(dayEntry, dateIndex) in formattedShoppingCart" :key="dateIndex"
              class="d-flex flex-column mb-3">
              <span class="text-subtitle-2"><v-icon small class="mr-2">mdi-calendar</v-icon>
                {{ Date.formatFullShortRelative(dayEntry.date) }}</span>
              <div v-for="(timeslot, timeIndex) in dayEntry.timeslots" :key="timeIndex" class="d-flex flex-column mb-1">
                <span class="d-flex align-center text-body-2 font-weight-bold ml-0"><v-icon small
                    class="mr-2">mdi-clock-outline</v-icon>
                  {{ formatTimeOnly(timeslot.start) }} -
                  {{ formatTimeOnly(timeslot.end) }}</span>
                <div v-for="(staffId, staffIdx) in timeslot.staffIds" :key="staffIdx"
                  class="d-flex flex-row justify-space-between align-center pl-7 text-body-2" style="min-height: 24px">
                  <span class="text-break">{{ storedStaffNames[staffId] }}</span>
                  <v-btn icon x-small @click="
                    removeBooking(
                      dayEntry.date,
                      timeslot.start,
                      timeslot.end,
                      staffId
                    )
                    " :disabled="isReserving">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-if="formattedShoppingCart.length && reservationRemainingTime" class="text-caption grey--text mt-2">
              {{ $translate('Booking is reserved for {0}').format(reservationRemainingTime) }}
            </div>
            <v-btn v-if="formattedShoppingCart.length" text small color="error" class="mt-2 px-0"
              @click="clearAllReservedTimes">
              {{ $translate('Remove all reserved times') }}
            </v-btn>
          </v-card-text>
          <v-card-text class="pt-0">
            <i><v-icon dense>mdi-pin-outline</v-icon>{{ $translate('MYBUSINESS_RESOURCE_RESERVATION_INFO') }}.</i>
          </v-card-text>
        </v-card>
        <v-btn large color="primary" @click="showCheckout = true" :disabled="formattedShoppingCart.length == 0">
          {{ $translate('Go forward for payment') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import StaffInfoDialog from "../StaffInfoDialog.vue";
import FilterButtonRow from "../FilterButtonRow.vue";
import DatePicker from "../DatePicker.vue";
import * as Sentry from "@sentry/browser";
import '../../dateextensions.js';

import { getName, mergeBaskets, getRandomId, formatPrice, decodeJson2 } from "../../util.js";
import { DAY_PERIODS } from "../../util.js";
import ExpansionPanelHeader from "../../views/ExpansionPanelHeader.vue";
import AdditionalProductList from "../../views/AdditionalProductList.vue";

import BottomDialog from "../BottomDialog.vue";
import Checkout from "../../views/Checkout.vue";
import Stepper from "../../views/Stepper.vue";
import StepperContent from "../../views/StepperContent.vue";
import OptionalResources from "../../views/OptionalResources.vue";
import WeekOverviewGrid from "./WeekOverviewGrid.vue";

export default {
  name: "zoezi-resourcebookingnew",
  components: {
    StaffInfoDialog,
    FilterButtonRow,
    DatePicker,
    StepperContent,
    AdditionalProductList,
    ExpansionPanelHeader,
    Stepper,
    BottomDialog,
    Checkout,
    OptionalResources,
    WeekOverviewGrid,
  },
  zoezi: {
    title: "Resource Booking Flow",
    icon: "mdi-account-box-outline",
  },
  data: () => ({
    selectedDate: Date.today().yyyymmdd(),
    showDatePicker: false,
    selectedDayPeriods: [],
    almostdone: false,

    viewMode: 'day',            // 'day' | 'week'
    selectedWeekStart: null,    // 'YYYY-MM-DD' (Monday) — set in created()
    showWeekPicker: false,
    weekSlots: [],              // raw slots for the visible week: [{start, end, staffs:[ids]}]
    isWeekLoading: false,

    services: [],
    resourceSlots: [],
    selectedCategory: null,
    selectedService: null,
    selectedResourceIds: [],
    selectedSlotIds: [],
    selectedSlotColumns: [],
    selectedTagIds: [],
    uniqueStarts: new Set(),
    reservations: [],
    reservationExpiry: null,
    reservationNow: new Date(),
    renewTimer: null,
    clockTimer: null,
    extraPersons: [],
    dayPeriods: DAY_PERIODS,

    showCheckout: false,
    checkoutLoading: false,
    currentStep: 0,
    cost: null,
    readyToPay: false,
    readyToPayExceptPaymentMethod: false,
    reserving: false,
    expandedQuestionPanels: [],
    storedStaffNames: {},
    
    isDialogShown: false,
    dialogStaffId: null,
    infoResourceTitle: null,
    infoResourceDescription: null,
    infoResourceImageKey: null,
    bookingId: getRandomId(8),

    products: [],
    expandedProductPanel: 0,
    showBookTimesSheet: false,
    unsubscribe: null,
    animateDone: false,
    reloading: false,
    requestIndex: 1,
    throttling: null,
    seatsLeft: null,
    checkingAvailability: false,
    selectedSlotHasCapacity: null,

    bulkSelecting: false,
    suppressRealtime: false,

    isMobile: false,
  }),
  props: {
    categoryId: {
      title: "Category",
      type: Number,
      fulltype: "select",
      typeinfo: {
        multiple: false,
        text: "name",
        value: "id",
        filter: (category, props) => {
          const hasServices =
            Array.isArray(category.services) && category.services.length > 0;

          if (!props.globalSiteSetting) {
            return hasServices;
          }

          return category.sites.includes(window.$store.state.selectedSiteId);
        },
        getItems: async () => {
          return await window.$booking.getResourceBookingCategories();
        },
        selectFirst: false,
      },
    },
    categoryTitle: {
      title: "Category title",
      type: String,
      default: "Category",
    },
    serviceId: {
      title: "Service",
      type: [Number, Array],
      fulltype: "select",
      typeinfo: {
        multiple: false,
        text: "name",
        value: "id",
        filter: (x, props) => {
          const matchesCategory = props.categoryId
            ? x.category_id == props.categoryId
            : true;
          const isShown = x.show;

          if (!props.globalSiteSetting || !props.selectedSiteId) {
            return matchesCategory && isShown;
          }

          const matchesSite =
            (x.site_id && x.site_id === props.selectedSiteId) ||
            (Array.isArray(x.sites) && x.sites.includes(props.selectedSiteId));

          return matchesCategory && isShown && matchesSite;
        },
        getItems: () =>
          window.$zoeziapi.get("/api/resourcebooking/service/get/all"),
      },
      default: () => [],
    },
    serviceTitle: {
      title: "Service title",
      type: String,
      default: "Service",
    },
    resourceStyle: {
        title: 'Appearance',
        type: Number,
        fulltype: 'select',
        typeinfo: {
            text: 'name',
            value: 'id',
            clearable: false,
            placeholder: 'Standard',
            getItems: () => ([
                {id: 0, name: 'Hide resource'},
                {id: 1, name: 'Large cards'},
                {id: 2, name: 'Compact cards'}
            ].map(x => ({...x, name: window.$translate(x.name)})))
        },
        default: () => 1
    },
    globalSiteSetting: {
      title: "Filter by globally selected site",
      type: Boolean,
      default: false,
    },
    showTags: {
      title: "Show tags filter",
      type: Boolean,
      default: true,
    },
    tagFilterMode: {
      title: "Tag filter mode",
      type: String,
      fulltype: "select",
      typeinfo: {
        text: "name",
        value: "id",
        clearable: false,
        getItems: () => [
          { id: "any", name: window.$translate("Show result with ANY selected tag") },
          { id: "all", name: window.$translate("Show result with ALL selected tags") },
        ],
        visible: 'showTags'
      },
      default: () => "any",
    },
    filterSites: {
      title: 'Filter by site',
      type: Array,
      fulltype: 'select',
      typeinfo: {
        getItems() {
          return window.$zoeziapi.get('/api/public/settings/get').then(settings => {
            return settings.sites.sort((a,b) => a.name.localeCompare(b.name));
          });
        },
        text: 'name',
        value: 'id',
        multiple: true,
        visible: '!globalSiteSetting'
      },
      default: () => []
    },
    showWeekend: {
      title: "Show weekend",
      type: Boolean,
      default: true,
    },
    viewSetting: {
      title: "Views shown",
      type: String,
      fulltype: 'select',
      typeinfo: {
        text: 'name',
        value: 'id',
        clearable: false,
        getItems: () => ([
          { id: 'both', name: window.$translate('Both day and week') },
          { id: 'day', name: window.$translate('Only day view') },
          { id: 'week', name: window.$translate('Only week view') },
        ]),
        // The week grid needs an explicit resource group, which only the picker can
        // produce — so week view isn't offerable while the picker is hidden.
        filter: (item, props) => item.id !== 'week' || (props && props.resourceStyle) !== 0,
        hint: 'Week view requires the resource picker to be shown',
      },
      default: () => 'both',
    }
  },

  watch: {
    reservationKeys(keys, previous) {
      // First key — renew now so the countdown has a value before the first interval.
      if (keys.length && !previous.length) {
        this.renewReservations();
      } else if (!keys.length) {
        this.reservationExpiry = null;
      }
    },
    "$store.state.selectedSiteId": {
      async handler() {
        this.selectedCategory = null;
        await this.reset();
        await this.getServices();
        await this.getFreeSlots();
      },
      immediate: true,
    },
    categoryId: {
      immediate: true,
      async handler(newCategoryId) {
        if (newCategoryId !== null && newCategoryId !== "") {
          if (this.categories.length === 0) {
            if (
              !this.$store.state?.settings?.resourcebookingcategories?.length
            ) {
              await this.$store.dispatch("fetchResourceBookingCategories");
            }
          }
          const foundCategory = this.categories.find(
            (c) => c.id === newCategoryId
          );
          if (foundCategory) {
            this.selectedCategory = foundCategory;
            await this.getServices(foundCategory.id);
          }
        } else {
          this.selectedCategory = null;
        }
      },
    },
    serviceId: {
      immediate: true,
      async handler(newServiceId) {
        if (newServiceId !== null && newServiceId !== "") {
          if (this.selectedCategory && this.services.length === 0) {
            await this.getServices(this.selectedCategory.id);
          } else if (!this.selectedCategory && this.categoryId) {
            const foundCategory = this.categories.find(
              (c) => c.id === this.categoryId
            );
            if (foundCategory) {
              this.selectedCategory = foundCategory;
              await this.getServices(foundCategory.id);
            }
          }

          const foundService = this.services.find((s) => s.id === newServiceId);
          if (foundService) {
            this.selectedService = foundService;
            if (
              !this.selectedCategory ||
              this.selectedCategory.id !== foundService.category_id
            ) {
              this.selectedCategory = this.categories.find(
                (c) => c.id === foundService.category_id
              );
            }
            this.resetSelections();
            await this.getFreeSlots();
          } else {
            this.selectedService = null;
            this.resetSelections();
          }
        } else {
          this.selectedService = null;
          this.resetSelections();
        }
      },
    },
    isDialogShown(newValue) {
      if (newValue !== this.isDialogShown) {
        this.$emit("input", newValue);
      }
    },
    async selectedDate() {
      this.scrollToBookingIfHidden();
      await this.getFreeSlots();
    },
    viewMode(mode) {
      this.showDatePicker = false; // avoid an orphaned date-picker overlay after switching views
      if (mode === 'week') this.fetchWeekSlots();
    },
    viewSetting() {
      this.normalizeViewMode();
    },
    resourceStyle() {
      this.normalizeViewMode();
    },
    selectedWeekStart() {
      if (this.viewMode === 'week') this.fetchWeekSlots(false);
    },
    selectedResourceIds() {
      if (this.viewMode === 'week') this.fetchWeekSlots(false);
    },
    async selectedCategory(category, oldCategory) {
      if (category && category.id !== (oldCategory ? oldCategory.id : null)) {
        if (
          !this.selectedService ||
          this.selectedService.category_id !== category.id
        ) {
          this.selectedService = null;
        }
        this.unreserveAll();
        this.resetSelections();
        await this.getServices(category.id);
      } else if (category === null && oldCategory !== null) {
        this.services = [];
        this.selectedService = null;
        this.unreserveAll();
        this.resetSelections();
      }
    },
    async selectedService(newValue, oldValue) {
      if (newValue && (!oldValue || newValue.id !== oldValue.id)) {
        this.resetSelections();
        await this.getFreeSlots();
        if (this.viewMode === 'week') this.fetchWeekSlots(false);
      } else if (newValue === null && oldValue !== null) {
        this.resetSelections();
        this.resourceSlots = {};
      }
    },
    reservations() {
      if (this.reservations && this.reservations.length) {
          if (this.category && !this.category.multiBooking) {
              this.currentStep = 0;
              this.showCheckout = true;
          } else {
              if (this.category && this.category.multiBooking) {
                  this.showBookTimesSheet = true;
              }
          }
      } else {
          this.showBookTimesSheet = false;
      }

      this.reservations.forEach(r => {
          let service = this.services.find(s => s.id == r.bookings[0].rbservice_id);
          if (!r.questions) {
              let questions = {};
              service.extendedInfo.forEach(q => {
                  q = Object.assign({}, q, {value: '', title: q.name});
                  questions[q.name] = q;
              })
              this.$set(r, 'questions', questions);
          }

          if (!r.basket) {
              this.$set(r, 'basket', {})
          }

          if (service.additional_products) {
              service.additional_products.forEach(id => this.$set(r.basket, id, []));
              let ids = String(service.additional_products.filter(id => !this.products.find(p => p.id == id)));
              if (ids) {
                  this.$store.dispatch('loadProducts').then(allProducts => {
                      let products = allProducts.filter(p => ids.includes(String(p.id)));
                      products.forEach(p => {
                          if (!this.products.find(x => x.id == p.id)) {
                              this.products.push(p);
                          }
                      })
                  });
              }
          }
      })

      this.expandedQuestionPanels = this.reservationsWithQuestions
        .map((r, index) => Object.values(r.questions).some(q => q.required) ? index : null)
        .filter(i => i !== null);
    },
    resources: {
      immediate: true,
      handler(newResources) {
        for (const resource of newResources) {
          this.storedStaffNames[resource.resourceId] = resource.title;
        }
      },
    },
  },

  computed: {
    activeFilterSites() {
      if (this.globalSiteSetting) {
        const siteId = this.$store.state.selectedSiteId;
        return siteId ? [siteId] : [];
      }

      if (!this.filterSites || this.filterSites.length === 0) {
        return [];
      }

      return this.filterSites.filter(site => site !== null && site !== undefined);
    },
    optionalCtx() {
      const steps = this.steps;
      const i = this.currentStep;

      const empty = {
        resource_name: "",
        resource_ids: [],
        start: null,
        end: null,
        service: { resources: [] },
        reservation: null,
        groupBookings: [],
        selected_resource_ids: [],
        pending_selections: [],
      };

      if (!Array.isArray(steps) || i < 0 || i >= steps.length) return empty;
      const c = steps[i] && steps[i].context ? steps[i].context : null;
      if (!c) return empty;

      return {
        resource_name: c.resource_name || "",
        resource_ids: Array.isArray(c.resource_ids) ? c.resource_ids : [],
        start: c.start || null,
        end: c.end || null,
        service: c.service || { resources: [] },
        reservation: c.reservation || null,
        groupBookings: Array.isArray(c.groupBookings) ? c.groupBookings : [],
        selected_resource_ids: Array.isArray(c.selected_resource_ids) ? c.selected_resource_ids : [],
        pending_selections: Array.isArray(c.pending_selections) ? c.pending_selections : [],
      };
    },
    selectedSlotKeys() {
      const s = new Set();
      for (const r of this.reservations) {
        for (const b of r.bookings) s.add(`${b.start}|${b.staff_id}`);
      }
      return s;
    },
    formattedShoppingCart() {
      const cart = {};

      this.reservations.forEach((reservation) => {
        reservation.bookings.forEach((booking) => {
          const bookingDate = booking.start.split(" ")[0];
          const startTime = booking.start.split(" ")[1];
          const endTime = booking.end.split(" ")[1];
          const staffId = booking.staff_id;

          if (!cart[bookingDate]) {
            cart[bookingDate] = {
              date: bookingDate,
              timeslots: [],
            };
          }

          let existingTimeslot = cart[bookingDate].timeslots.find(
            (ts) => ts.start === startTime && ts.end === endTime
          );

          if (existingTimeslot) {
            if (!existingTimeslot.staffIds.includes(staffId)) {
              existingTimeslot.staffIds.push(staffId);
            }
          } else {
            cart[bookingDate].timeslots.push({
              start: startTime,
              end: endTime,
              staffIds: [staffId],
            });
          }
        });
      });

      const sortedCart = Object.values(cart).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      sortedCart.forEach((dayEntry) => {
        dayEntry.timeslots.sort((a, b) => {
          const timeA = new Date(`2000/01/01 ${a.start}`);
          const timeB = new Date(`2000/01/01 ${b.start}`);
          return timeA - timeB;
        });
      });

      return sortedCart;
    },
    noCategoriesFound() {
      return (
        this.categories.length === 0 &&
        (this.activeFilterSites.length > 0 ||
          (this.globalSiteSetting && !this.$store.state.selectedSiteId))
      );
    },
    seatsLeftText() {
      if (this.seatsLeft == 1) {
        return this.$translate("There is currently 1 spot left");
      }
      return this.$translate("There are currently {0} seats left").format(this.seatsLeft);
    },
    reservationsWithQuestions() {
      return this.reservations.filter(
        (r) => r.questions && Object.keys(r.questions).length
      );
    },
    allQuestionsDone() {
      return this.reservationsWithQuestions.every(r =>
        Object.values(r.questions).every(q => {
          if (!q.required) return true;
          const v = q.value;
          if (v == null) return false;
          if (q.type === 'checkbox') return v === true;
          if (q.type === 'multiselect' || q.type === 'multicheckbox') return Array.isArray(v) && v.length > 0;
          return v !== '' && v !== false;
        })
      );
    },
    DAY_PERIODS() {
      return DAY_PERIODS;
    },
    isDateToday() {
      return this.selectedDate === Date.today().yyyymmdd();
    },
    compactDay() {
      // phones + tablets: compact per-resource time lists instead of the aligned
      // grid, so late-starting resources don't hide behind empty placeholder cells
      return this.$vuetify.breakpoint.smAndDown;
    },
    weekViewAvailable() {
      // "Hide resource" removes the picker, so the visitor can't form a group and
      // the week grid would have nothing to render — day view is the only usable mode.
      return this.resourceStyle !== 0;
    },
    weekDayCount() {
      return this.showWeekend ? 7 : 5;
    },
    weekDayDates() {
      // Monday..Friday (or ..Sunday when showWeekend) as ['YYYY-MM-DD', ...]
      const out = [];
      for (let i = 0; i < this.weekDayCount; i++) {
        out.push(Date.newFull(this.selectedWeekStart).addDays(i).yyyymmdd());
      }
      return out;
    },
    weekDaysForGrid() {
      const WEEKDAYS = [this.$translate('Mon'), this.$translate('Tue'), this.$translate('Wed'),
                        this.$translate('Thu'), this.$translate('Fri'), this.$translate('Sat'), this.$translate('Sun')];
      return this.weekDayDates.map((d, i) => {
        const nd = Date.newFull(d);
        return { date: d, weekday: WEEKDAYS[i], label: `${nd.getDate()}/${nd.getMonth() + 1}` };
      });
    },
    weekRangeLabel() {
      const first = this.weekDayDates[0];
      const last = this.weekDayDates[this.weekDayDates.length - 1];
      return `${first} – ${last}`;
    },
    isWeekAtCurrent() {
      return this.selectedWeekStart === this.mondayOf(Date.today().yyyymmdd());
    },
    weekGroupResources() {
      // The group = the resources explicitly picked in "Välj resurs". If none are
      // picked, the week view shows nothing (handled by the template + fetch guard).
      if (!this.selectedResourceIds.length) return [];
      const staffs = (this.selectedService && this.selectedService.staffs) || [];
      const wanted = new Set(this.selectedResourceIds);
      return staffs
        .filter(s => wanted.has(s.id))
        .filter(s => {
          if (!this.selectedTagIds.length) return true;
          const rt = (s.tags || []).map(t => t.id);
          return this.tagFilterMode === 'all'
            ? this.selectedTagIds.every(id => rt.includes(id))
            : rt.some(id => this.selectedTagIds.includes(id));
        })
        .map(s => ({ resourceId: s.id, serviceId: this.selectedService.id, sites: s.sites, xstaffs: this.selectedService.xstaffs }));
    },
    weekGroupIds() {
      return this.weekGroupResources.map(r => r.resourceId);
    },
    weekSlotByKey() {
      // "YYYY-MM-DD HH:MM" -> slot
      const map = {};
      for (const s of this.weekSlots) {
        const d = Date.newFull(s.start);
        map[`${d.yyyymmdd()} ${d.hhmm()}`] = s;
      }
      return map;
    },
    weekSelectedKeys() {
      // "YYYY-MM-DD|HH:MM" where the whole group is already reserved. Derived from
      // reservations (not free slots) so a booked cell stays "Vald" after refetch.
      const group = this.weekGroupIds;
      if (!group.length) return new Set();
      const weekDates = new Set(this.weekDayDates);
      const byKey = {};
      for (const r of this.reservations) {
        for (const b of r.bookings) {
          const d = Date.newFull(b.start);
          const date = d.yyyymmdd();
          if (!weekDates.has(date)) continue;
          const key = `${date}|${d.hhmm()}`;
          (byKey[key] = byKey[key] || new Set()).add(b.staff_id);
        }
      }
      const sel = new Set();
      for (const key in byKey) {
        if (group.every(id => byKey[key].has(id))) sel.add(key);
      }
      return sel;
    },
    weekTimeRows() {
      const set = new Set();
      for (const s of this.weekSlots) set.add(Date.newFull(s.start).hhmm());
      // include reserved times so a booked cell's row doesn't disappear after refetch
      for (const r of this.reservations)
        for (const b of r.bookings) {
          const d = Date.newFull(b.start);
          if (this.weekDayDates.includes(d.yyyymmdd())) set.add(d.hhmm());
        }
      return Array.from(set).sort();
    },
    weekTimeRowsForGrid() {
      // Row label shows the booked interval (start–end from the slot duration),
      // e.g. "09:00–09:55", so the user sees the length before clicking. Always
      // HH:MM on both sides so rows don't mix formats within the same column.
      const endByStart = {};
      for (const s of this.weekSlots) {
        endByStart[Date.newFull(s.start).hhmm()] = Date.newFull(s.end).hhmm();
      }
      for (const r of this.reservations)
        for (const b of r.bookings) {
          endByStart[Date.newFull(b.start).hhmm()] = Date.newFull(b.end).hhmm();
        }
      return this.weekTimeRows.map(t => {
        const end = endByStart[t];
        return { time: t, label: end ? `${t}–${end}` : t };
      });
    },
    weekCellStates() {
      // key "YYYY-MM-DD|HH:MM" -> 'bookable' | 'selected' | 'none'
      const group = this.weekGroupIds;
      const states = {};
      for (const date of this.weekDayDates) {
        for (const time of this.weekTimeRows) {
          const key = `${date}|${time}`;
          let state = 'none';
          if (this.weekSelectedKeys.has(key)) {
            state = 'selected';
          } else {
            const slot = this.weekSlotByKey[`${date} ${time}`];
            if (slot && group.length && group.every(id => slot.staffs.includes(id))) state = 'bookable';
          }
          states[key] = state;
        }
      }
      return states;
    },
    daysWithSelection() {
      const s = new Set();
      for (const r of this.reservations)
        for (const b of r.bookings) {
          if (this.isWeekGroupBooking(b)) s.add(String(b.start).split(' ')[0]);
        }
      return s;
    },
    selectedDaysList() {
      return Array.from(this.daysWithSelection);
    },
    categories() {
      const filterOnSites = this.activeFilterSites;

      if (this.globalSiteSetting && !this.$store.state.selectedSiteId && filterOnSites.length === 0) {
        return [];
      }
      
      let categories = [];
      for (let cat of this.$store.state?.settings?.resourcebookingcategories || []) {
        if (cat.services && cat.services.length !== 0) {
          if (filterOnSites.length > 0) {
            const hasServicesOnSites = cat.sites.some(siteId => filterOnSites.includes(siteId));
            if (!hasServicesOnSites) {
              continue;
            }
          }
          categories.push(cat);
        }
      }
      return categories;
    },

    allResources() {
      return Object.entries(this.resourceSlots)
        .filter(
          ([id]) =>
            this.selectedService &&
            this.selectedService.staffs.map(s => s.id).includes(Number(id))
        )
        .map(([id, resource]) => ({ id: Number(id), ...resource }))
        .sort((a, b) => a.title.localeCompare(b.title));
    },

    resources() {
      if (!this.selectedTagIds.length) return this.allResources;
      
      if (this.tagFilterMode === "all") {
        // AND logic: resource must have ALL selected tags
        return this.allResources.filter(r => {
          const resourceTagIds = (r.tags || []).map(t => t.id);
          return this.selectedTagIds.every(selectedId => resourceTagIds.includes(selectedId));
        });
      } else {
        // OR logic (default): resource must have ANY of the selected tags
        return this.allResources.filter(r =>
          (r.tags || []).some(t => this.selectedTagIds.includes(t.id))
        );
      }
    },

    selectedResources() {
      if (this.selectedResourceIds.length === 0) {
        return this.resources;
      }
      return this.resources.filter(resource =>
        this.selectedResourceIds.includes(resource.resourceId)
      );
    },
    filteredServices() {
      if (!this.selectedCategory) {
        return [];
      }
      return this.services
        .filter((service) => service.category_id === this.selectedCategory.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    availableResourceTags() {
      const tags = [];
      for (const r of this.allResources) {
        (r.tags || []).forEach(t => {
          if (!tags.find(x => x.id === t.id)) tags.push(t);
        });
      }
      return tags.sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredResources() {
      const passTagFilter = (resource) => {
        if (!this.selectedTagIds.length) return true;
        const rt = resource.tags || [];
        return rt.some(t => this.selectedTagIds.includes(t.id));
      };

      return this.selectedResources
        .filter(resource => passTagFilter(resource))
        .filter(resource => resource.slots.length !== 0);
    },
    slots() {
      let sortedResults = [];
      let uniqueStarts = new Set();

      for (let resource of this.selectedResources) {
        for (let freeSlot of resource.slots) {
          if (!uniqueStarts.has(freeSlot.start)) {
            uniqueStarts.add(freeSlot.start);
          }

          sortedResults.push({
            start: freeSlot.start,
            end: freeSlot.end,
            period: this.getPeriod(freeSlot.start),
            resourceId: resource.resourceId,
            sites: resource.sites,
            serviceId: resource.serviceId,
          });
        }
      }

      this.$nextTick(() => {
        this.uniqueStarts = uniqueStarts;
      });

      sortedResults.sort((a, b) => new Date(a.start) - new Date(b.start));
      return sortedResults;
    },
    filteredSlots() {
      if (this.selectedDayPeriods.length === 0) {
        return this.slots;
      } else {
        return this.slots.filter((slot) =>
          this.selectedDayPeriods.includes(slot.period.id)
        );
      }
    },

    isColumnSelected() {
      return (slotStart) => {
        const activeResources = this.filteredResources;

        if (activeResources.length === 0) {
          return false;
        }

        const allResourcesForThisTimeAreSelected = activeResources.every(
          (resource) => {
            const hasSlot = resource.slots.some((s) => s.start === slotStart);
            if (!hasSlot) {
              return true;
            }
            return this.isSlotSelected(slotStart, resource.resourceId);
          }
        );

        return allResourcesForThisTimeAreSelected;
      };
    },
    staffs() {
      let staffs = [];
      this.services.forEach((s) => {
        s.staffs.forEach((staff) => {
          staff.name = getName(staff);
          if (!staffs.find((s) => s.id == staff.id)) {
            staffs.push({
              id: staff.id,
              name: staff.name,
              imagekey: staff.imagekey,
              sites: staff.sites,
            });
          }
        });
      });
      return staffs;
    },
    additionalProducts() {
      let res = {};
      this.reservations.forEach((r) => {
        let service = this.services.find(
          (s) => s.id == r.bookings[0].rbservice_id
        );
        res[r.key] = (service.additional_products || [])
          .map((product_id) =>
            this.products.find((product) => product.id == product_id)
          )
          .filter((x) => x);
      });

      return res;
    },
    additionalProductsComplete() {
      // Only validate user selection if user is logged in
      if (!this.$store.state.user) {
        return true;
      }

      // Check if all training cards/memberships in additional products have users selected
      for (let r of this.reservations) {
        let additionalProds = this.additionalProducts[r.key] || [];
        for (let product of additionalProds) {
          if (!product) continue;
          if (['trainingcard', 'membership'].includes(product.type)) {
            let basketItems = r.basket[product.id] || [];
            for (let bi of basketItems) {
              if (!bi.count) continue;
              // Check if users array exists and all users are selected
              if (!bi.users || bi.users.length < bi.count) {
                return false;
              }
              for (let i = 0; i < bi.count; i++) {
                if (!bi.users[i]) {
                  return false;
                }
              }
            }
          }
        }
      }
      return true;
    },
    checkoutItems() {
      if (this.reservations.length == 0) {
        return [];
      }

      let items = this.reservations.map((r, index) => ({
        count: 1 + this.extraPersons.length,
        rbservice_id: r.bookings[0].rbservice_id,
        service: this.services.find((s) => s.id == r.bookings[0].rbservice_id),
        reservation: r.key,
        slots: r.bookings.map((b) => ({
          start: b.start,
          end: b.end,
          staff_id: b.staff_id,
          rbservice_id: b.rbservice_id,
          site_id: b.site_id,
          optional_resources: b.optional_resources,
          count: b.count,
        })),
        extra_person: this.extraPersons,
        context: {
          service: this.services.find(
            (s) => s.id == r.bookings[0].rbservice_id
          ),
          start: Date.newFull(r.bookings[0].start),
          staff: this.staffs.find((s) => s.id == r.bookings[0].staff_id),
          period: this.getPeriod(r.bookings[0].start),
        },
        extended_info: Object.fromEntries(
          Object.values(r.questions || {}).map((q) => [q.name, q.value])
        ),
        tmp_id: this.bookingId + "-" + (index + 1),
        site_id: r.bookings[0].site_id,
      }));

      this.reservations.forEach((r, index) => {
        this.additionalProducts[r.key].forEach((product) => {
          let b = r.basket[product.id];
          b.forEach((bi) => {
            if (bi.count) {
              items.push({
                count: bi.count,
                rbooking_id: this.bookingId + "-" + (index + 1),
                price: product.price,
                product_id: product.id,
                variant: bi.variant,
                users: bi.users,
                site_id: r.bookings[0].site_id,
              });
            }
          });
        });
      });

      return items;
    },
    isReserving() {
      return !!this.reservations.find(
        (r) => !!r.bookings.find((b) => b.reserving)
      );
    },
    reservationKeys() {
      return this.reservations.map((r) => r.key).filter((key) => key);
    },
    reservationRemainingTime() {
      if (!this.reservationExpiry) {
        return "";
      }
      const diff = this.reservationExpiry - this.reservationNow;
      if (diff <= 0) {
        return "";
      }
      if (diff <= 60000) {
        const seconds = (diff / 1000).toFixed(0);
        return seconds == 1
          ? "1 " + this.$translate("SECOND_ONE")
          : seconds + " " + this.$translate("seconds");
      }
      const minutes = Math.floor(diff / 60000);
      return minutes == 1
        ? "1 " + this.$translate("minute")
        : minutes + " " + this.$translate("minutes");
    },
    showPrice() {
      return !!this.$store.state.settings?.modules?.Payment;
    },
    productsInBasket() {
      return mergeBaskets(this.reservations);
    },
    contactinfo() {
      if (!this.$store.state.settings) {
        return {};
      }

      if (this.$store.state.settings?.sites?.length > 1) {
        let site = this.$store.state.settings.sites.find(
          (x) => x.id == this.siteId
        );
        return {
          visit_address: site?.address,
          visit_zipCode: site?.zipCode,
          visit_city: site?.city,
        };
      } else {
        return this.$store.state.settings.contactinfo;
      }
    },
    steps() {
      if (this.reservations.length == 0) {
        return [];
      }
      let has_questions = this.reservations.find(
        (r) =>
          this.services.find((s) => s.id == r.bookings[0].rbservice_id)
            .extendedInfo.length
      );
      let has_additional_products = this.reservations.find(
        (r) =>
          this.services.find((s) => s.id == r.bookings[0].rbservice_id)
            .additional_products?.length
      );

      let optional_resources_steps = [];
      this.reservations.forEach((r) => {
        const service = this.services.find((s) => s.id == r.bookings[0].rbservice_id);
        if (!service) return;

        if (service.resources?.length && service.customer_selectable_resources) {
          // staff_id + date (YYYY-MM-DD)
          const groups = new Map(); // key: `${staff_id}|${date}` -> bookings[]

          r.bookings.forEach((b) => {
            const date = new Date(b.start).yyyymmdd();
            const key = `${b.staff_id}|${date}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(b);
          });

          for (const [key, bookings] of groups.entries()) {
            const staffId = Number(key.split("|")[0]);
            const staff = this.staffs.find((s) => s.id == staffId);
            const name  = staff ? staff.name : this.storedStaffNames[staffId] || this.$translate("Resource");
            const title = `${name}`;

            const intersection = (() => {
              if (bookings.length === 0) return [];

              // Start with the optional resources of the first booking
              const commonResources = new Set(bookings[0].optional_resources || []);

              // Compare against the rest of the bookings
              for (let i = 1; i < bookings.length; i++) {
                const currentResources = new Set(bookings[i].optional_resources || []);
                for (const resource of commonResources) {
                  if (!currentResources.has(resource)) {
                    commonResources.delete(resource);
                  }
                }
              }

              return [...commonResources];
            })();

            const sortedStarts = bookings.map(b => b.start).sort();
            const sortedEnds   = bookings.map(b => b.end).sort();
            const groupStart   = sortedStarts[0];
            const groupEnd     = sortedEnds[sortedEnds.length - 1];

            // Selections in OTHER slots of the booking constrain what's still available
            // here, since an optional resource has limited capacity at a given time.
            const groupBookingsSet = new Set(bookings);
            const pending_selections = [];
            this.reservations.forEach((res) => {
              res.bookings.forEach((b) => {
                if (groupBookingsSet.has(b)) return;
                (b.optional_resources || []).forEach((resource_id) => {
                  pending_selections.push({ resource_id, start: b.start, end: b.end });
                });
              });
            });
            // Also account for bookings already placed in the basket — they are
            // separate reservations for the same checkout and consume capacity too.
            (this.$store.state.carts["resursbokning"]?.items || []).forEach((item) => {
              (item.slots || []).forEach((slot) => {
                (slot.optional_resources || []).forEach((resource_id) => {
                  pending_selections.push({ resource_id, start: slot.start, end: slot.end });
                });
              });
            });

            optional_resources_steps.push({
              title,
              id: "optional_resources",
              context: {
                resource_name: name,
                start: groupStart,
                end: groupEnd,
                rbservice_id: bookings[0].rbservice_id,
                service,
                resource_ids: service.resources.map(rr => rr.id),
                reservation: r,
                groupBookings: Array.isArray(bookings) ? bookings : [],
                selected_resource_ids: intersection,
                pending_selections,
              },
              complete: true,
            });
          }
        }
      });
      const pay = this.$translate("Pay");

      let payButtonTitle =
        this.cost > 0
          ? `${pay} ${formatPrice(this.cost)}`
          : this.$translate("Confirm booking");
      let payTitle = this.cost > 0 ? pay : this.$translate("Confirm booking");
      let showExtraPersons = this.selectedSlotHasCapacity;
      let personsHaveName = this.extraPersons.every((p) => p.name);
      let cartDisabled = !this.readyToPayExceptPaymentMethod;
      let extraButton = true && {
        label: this.$translate("Add to cart and continue shopping"),
        clicked: this.addToCart,
        disabled: cartDisabled,
      };

      return optional_resources_steps
        .concat([
          showExtraPersons && {
            title: this.$translate("Book more"),
            id: "extra_person",
            complete: personsHaveName,
          },
          has_questions && {
            title: this.$translate("Questions"),
            id: "questions",
            complete: this.allQuestionsDone,
          },
          has_additional_products && {
            title: this.$translate("Optional"),
            id: "products",
            complete: this.additionalProductsComplete,
          },
          {
            title: payTitle,
            id: "checkout",
            nextTitle: payButtonTitle,
            nextColor: "#7bbd1b",
            nextDark: true,
            nextClicked: this.pay,
            nextLoading: this.checkoutLoading,
            complete: this.readyToPay && this.$store.state.user,
            extraButton,
          },
          { title: this.$translate("Done"), id: "done" },
        ])
        .filter((x) => x);
    },
  },
  methods: {
    openGoogle() {
      const address = `${this.contactinfo.visit_address}, ${this.contactinfo.visit_city}`;
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    },
    optionalResourceSelectedGroup(reservation, groupBookings, resource_id, selected) {
      const previous = groupBookings.map((b) => b.optional_resources.slice());
      for (const b of groupBookings) {
        const i = b.optional_resources.indexOf(resource_id);
        if (selected) {
          if (i === -1) b.optional_resources.push(resource_id);
        } else {
          if (i !== -1) b.optional_resources.splice(i, 1);
        }
      }
      this.updateReservation(reservation, () =>
        groupBookings.forEach((b, i) =>
          this.$set(b, "optional_resources", previous[i])
        )
      );
    },
    isSlotSelected(start, resourceId) {
      return this.selectedSlotKeys.has(`${start}|${resourceId}`);
    },
    // The site the slot was listed for, not the staff's first site.
    siteIdFor(resource, slot) {
      return (slot && slot.site_id) || resource.sites[0];
    },
    buildBookingObj(resource, slot) {
      return {
        start: slot.start,
        end: slot.end,
        rbservice_id: resource.serviceId,
        staff_id: resource.resourceId,
        site_id: this.siteIdFor(resource, slot),
        optional_resources: [],
        count: 1,
      };
    },
    async getFreeSlots() {
      if (this.selectedService != null && this.selectedCategory != null) {
        // Fetches all available resources for the selected day and adds all available timeslots
        let start = this.selectedDate;
        let end = this.selectedDate;
        
        const activeSites = this.activeFilterSites;
        let sites = null;
        if (activeSites.length > 0) {
          sites = activeSites.join(',');
        }

        let slots = await this.$api.get("/api/public/resourcebooking/get", {
          service: this.selectedService.id,
          start: start,
          end: end,
          site: sites,
        });

        let timeslots = {};

        for (let resource of this.selectedService.staffs) {
          timeslots[resource.id] = {
            serviceId: this.selectedService.id,
            sites: resource.sites,
            resourceId: resource.id,
            title: resource.firstname
              ? resource.firstname + " " + resource.lastname
              : resource.lastname,
            slots: [],
            description: resource.description,
            imagekey: resource.imagekey,
            tags: resource.tags || []
          };
        }

        // Populating the slots from the api
        for (let slot of slots) {
          for (let resourceId of slot["staffs"]) {
            let slot_entry = {
              start: slot["start"],
              end: slot["end"],
              xstaffs: slot["xstaffs"] || [],
              site_id: slot["site_id"],
            };

            if (
              timeslots[resourceId] &&
              !timeslots[resourceId].slots.some(
                (s) => s.start === slot_entry.start && s.end === slot_entry.end
              )
            ) {
              timeslots[resourceId].slots.push(slot_entry);
            }
          }
        }

        // Include the users reserved slots in the results,
        for (const reservation of this.reservations) {
          for (const booking of reservation.bookings) {
            const bookingDate = booking.start.split(" ")[0];
            if (
              booking.rbservice_id === this.selectedService.id &&
              bookingDate === this.selectedDate
            ) {
              const resourceId = booking.staff_id;
              const slot_entry = {
                start: booking.start,
                end: booking.end,
                site_id: booking.site_id,
              };

              if (timeslots[resourceId]) {
                if (
                  !timeslots[resourceId].slots.some(
                    (s) =>
                      s.start === slot_entry.start && s.end === slot_entry.end
                  )
                ) {
                  timeslots[resourceId].slots.push(slot_entry);
                  timeslots[resourceId].slots.sort(
                    (a, b) => new Date(a.start) - new Date(b.start)
                  );
                }
              }
            }
          }
        }

        this.resourceSlots = timeslots;
      } else {
        this.resourceSlots = {};
      }
    },
    async getServices(categoryId) {
      const activeSites = this.activeFilterSites;
      let sites = null;
      if (activeSites.length > 0) {
        sites = activeSites.join(',');
      }
      
      this.services = await this.$api.get(
        "/api/public/resourcebooking/service/get",
        {
          category: categoryId,
          site: sites,
        }
      );
    },

    setServices(serviceIds) {
      this.selectedService = serviceIds.concat();
    },

    incrementDate() {
      this.selectedDate = Date.newFull(this.selectedDate).addDays(1).yyyymmdd();
    },
    decrementDate() {
      this.selectedDate = Date.newFull(this.selectedDate).addDays(-1).yyyymmdd();
    },
    dateChanged(newDate) {
      this.selectedDate = newDate;
      this.showDatePicker = false;
    },
    getPeriod(time) {
      let startHour = Date.newFull(time).getHours();
      return DAY_PERIODS.find(
        (date) => startHour >= date.start && startHour < date.end
      );
    },
    setItem(list, id, selected) {
      let i = list.indexOf(id);
      if (selected && i == -1) {
        list.push(id);
      } else if (!selected && i != -1) {
        list.splice(i, 1);
      }
    },
    toggleSlot(slot) {
      if (!this.isSlotSelected(slot.start, slot.resourceId)) {
        this.reserveSlot(slot);
      } else {
        this.unreserveSlot(slot);
      }
    },
    async deselectSlot(slot) {
      await this.unreserveSlot(slot);
    },
    hasSlotForStart(resource, start) {
      return resource.slots.some((slot) => slot.start === start);
    },
    // Desktop keeps the aligned grid (all unique starts, empty placeholders where a
    // resource has no slot). On smaller screens that means scrolling past empty
    // cells, so there we render only the resource's own start times — a compact list.
    startsForResource(resource) {
      if (this.compactDay) {
        return resource.slots.map((slot) => slot.start).sort();
      }
      return Array.from(this.uniqueStarts).sort();
    },
    handleSlotClick(resource, start) {
      const slot = resource.slots.find((slot) => slot.start === start);
      if (!slot) return;
      this.toggleSlot(
        {
          start: start,
          end: slot.end,
          resourceId: resource.resourceId,
          serviceId: resource.serviceId,
          sites: [this.siteIdFor(resource, slot)],
          xstaffs: slot.xstaffs,
        },
        resource.title
      );
    },
    getSlotColor(resource, start) {
      return this.isSlotSelected(start, resource.resourceId) ? 'success' : '';
    },
    handleColumnClick(start) {
      if (!this.isColumnSelected(start)) {
        this.selectSlotColumn(start);
      } else {
        this.deselectSlotColumn(start);
      }
    },
    getColumnButtonText(start) {
      return !this.isColumnSelected(start)
        ? this.$translate('Select all times')
        : this.$translate('Remove all times');
    },
    getQuestionPanelTitle(reservation) {
      const service = this.services.find(
        (s) => s.id == reservation.bookings[0].rbservice_id
      );
      return this.$translate("Supplementary questions for {0}").format(
        service ? service.name : ""
      );
    },
    optionalResourceSelected(reservation, booking, resource_id, selected) {
      const previousResources = booking.optional_resources.slice();
      let i = booking.optional_resources.indexOf(resource_id);
      if (!selected) {
        if (i != -1) {
          booking.optional_resources.splice(i, 1);
        }
      } else {
        if (i == -1) {
          booking.optional_resources.push(resource_id);
        }
      }

      this.updateReservation(reservation, () =>
        this.$set(booking, "optional_resources", previousResources)
      );
    },
    addExtraPerson() {
      const previousPersons = this.extraPersons.slice();
      this.extraPersons.push({
        name: this.$translate("Participant {0}").format(
          this.extraPersons.length + 2
        ),
      });
      this.updateExtraPersonCount(previousPersons);
    },
    removeExtraPerson(index) {
      const previousPersons = this.extraPersons.slice();
      this.extraPersons.splice(index, 1);
      this.updateExtraPersonCount(previousPersons);
    },
    // All or nothing: extraPersons is shared across reservations, so a partial failure
    // must put every one of them back or checkout books more people than are reserved.
    async updateExtraPersonCount(previousPersons) {
      const targets = this.reservations.slice();
      const previousCounts = new Map(targets.map((r) => [r, r.count]));
      const count = this.extraPersons.length + 1;

      targets.forEach((r) => this.$set(r, "count", count));
      const results = await Promise.all(
        targets.map((r) => this.updateReservation(r))
      );
      if (results.every((ok) => ok)) {
        return;
      }

      this.extraPersons = previousPersons;
      const revert = targets.filter((r) => this.reservations.includes(r));
      revert.forEach((r) => this.$set(r, "count", previousCounts.get(r)));
      await Promise.all(revert.map((r) => this.updateReservation(r)));
    },
    async checkAvailabilityForReservedSlots() {
      let bookings = this.reservations
        .map((r) =>
          r.bookings.map((b) => {
            let service = this.services.find((s) => s.id == b.rbservice_id);
            let resource_ids = service?.xstaffs?.map((x) => x.id) || [];
            resource_ids.push(b.staff_id);
            return {
              resource_ids: resource_ids,
              start: b.start,
              end: b.end,
              rbservice_id: b.rbservice_id,
              staff_id: b.staff_id,
            };
          })
        )
        .flat();

      if (bookings.length == 0) {
        this.seatsLeft = null;
        return;
      }

      this.checkingAvailability = true;

      try {
        // the endpoint caps at 20 slots per call, so request in chunks and merge
        // per resource (min availability, max capacity) — same aggregation the
        // backend does across bookings within a single call
        const CHUNK = 20;
        const availByPid = {};
        const capByPid = {};
        for (let i = 0; i < bookings.length; i += CHUNK) {
          const res = await this.$api.post('/api/resource/availability/slots/get', {
            bookings: bookings.slice(i, i + CHUNK),
            mode: 'full',
          });
          if (!res.ok) throw new Error(`availability ${res.status}`);
          const data = await res.json();
          for (const [pid, v] of Object.entries(data?.availability || {})) {
            availByPid[pid] = pid in availByPid ? Math.min(availByPid[pid], v) : v;
          }
          for (const [pid, v] of Object.entries(data?.capacity || {})) {
            capByPid[pid] = pid in capByPid ? Math.max(capByPid[pid], v) : v;
          }
        }

        const availability = Object.values(availByPid);
        const capacity   = Object.values(capByPid);
        if (!availability.length || !capacity.length) { this.seatsLeft = null; this.selectedSlotHasCapacity ??= false; return; }

        this.seatsLeft = availability.reduce((a,b) => Math.min(a,b), Infinity);
        if (this.selectedSlotHasCapacity === null) {
          const minCap = capacity.reduce((a,b) => Math.min(a,b), Infinity);
          this.selectedSlotHasCapacity = minCap > 1;
        }
      } catch (e) {
        Sentry.captureException(e);
        this.seatsLeft = null;
        this.selectedSlotHasCapacity ??= false;
      } finally {
        this.checkingAvailability = false;
      }

    },
    // Cart cleared mid-flight — release what we just took, nothing points at it now.
    releaseIfDetached(r, key) {
      if (this.reservations.includes(r)) {
        return false;
      }
      if (key) {
        this.$api.post("/api/resource/reservation/cancel", { key });
      }
      return true;
    },
    async isExpiredReservation(response) {
      try {
        const body = await response.json();
        return body?.type === "reservation_error";
      } catch (e) {
        return false;
      }
    },
    // A booking stuck on reserving: true keeps isReserving true, which disables the
    // whole flow. Only addedBookings are dropped, so a concurrent call keeps its own.
    async handleReservationFailure(r, addedBookings, response) {
      const expired = await this.isExpiredReservation(response);
      const idx = this.reservations.indexOf(r);

      if (expired) {
        // Already released server-side, and a dead key would fail the checkout call.
        if (idx !== -1) {
          this.reservations.splice(idx, 1);
        }
      } else {
        const kept = r.bookings.filter((b) => !addedBookings.includes(b));
        kept.forEach((b) => this.$set(b, "reserving", false));
        this.$set(r, "bookings", kept);

        if (!kept.length && idx !== -1) {
          this.reservations.splice(idx, 1);
          if (r.key) {
            this.$api.post("/api/resource/reservation/cancel", { key: r.key });
          }
        }
      }

      this.$store.commit(
        "showErrorDialog",
        expired
          ? {
              title: this.$translate("Reservation expired"),
              text: this.$translate(
                "The reservation expired, please select the times again"
              ),
            }
          : {
              title: this.$translate("Error during reservation"),
              text: this.$translate(
                "Something went wrong when the booking was to be reserved"
              ),
            }
      );
    },
    // Reservations time out per service, so picking in one loses the others silently.
    async renewReservations() {
      const keys = this.reservationKeys;
      if (!keys.length || this.checkoutLoading) {
        return;
      }

      try {
        const response = await this.$api.post(
          "/api/resource/reservation/renew",
          { key: keys }
        );
        if (!response.ok) {
          return;
        }

        const data = decodeJson2(await response.json());
        this.reservationExpiry = data.expire_utc
          ? new Date(
              data.expire_utc.getTime() -
                data.expire_utc.getTimezoneOffset() * 60000
            )
          : null;

        if (!data.result) {
          await this.dropExpiredReservations(data.expired_keys);
        }
      } catch (error) {
        // On an interval — an unparseable body must not reject every minute.
        Sentry.captureException(error);
      }
    },
    async dropExpiredReservations(expiredKeys) {
      const keys = expiredKeys || [];
      if (!keys.length) {
        return;
      }
      this.reservations = this.reservations.filter(
        (r) => !keys.includes(r.key)
      );

      if (!this.$store.state.errorDialog.show) {
        this.$store.commit("showErrorDialog", {
          title: this.$translate("Reservation expired"),
          text: this.$translate(
            "The reservation expired, please select the times again"
          ),
        });
      }
      // validate() flags a key before expire() releases it — release it now, or the
      // refetch shows the customer's own dead times as taken.
      await this.$api.post("/api/resource/reservation/cancel", { key: keys });
      this.getFreeSlots();
      this.refetchWeekSlotsIfActive();
    },
    startReservationTimers() {
      this.renewTimer = setInterval(this.renewReservations, 60000);
      this.clockTimer = setInterval(() => {
        // Only write when something is counting down, so an idle cart costs nothing.
        if (this.reservationExpiry) {
          this.reservationNow = new Date();
        }
      }, 1000);
    },
    stopReservationTimers() {
      if (this.renewTimer) {
        clearInterval(this.renewTimer);
        this.renewTimer = null;
      }
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
    },
    async clearAllReservedTimes() {
      if (this.checkoutLoading) {
        return;
      }
      // Clear locally first so the escape hatch works even if the cancel fails.
      const keys = this.reservations.map((r) => r.key).filter((key) => key);
      this.reservations = [];
      // Left behind, these would book N people against the next reservation's 1.
      this.extraPersons = [];

      if (keys.length) {
        await this.$api.post("/api/resource/reservation/cancel", { key: keys });
      }
      this.getFreeSlots();
      this.refetchWeekSlotsIfActive();
    },
    async reserveSlot(slot) {
      let reservedSlot = {
        start: slot.start,
        end: slot.end,
        rbservice_id: slot.serviceId,
        staff_id: slot.resourceId,
        reserving: true,
        site_id: slot.sites[0],
        optional_resources: [],
        count: 1,
      };

      if (slot.xstaffs && slot.xstaffs.length) {
        reservedSlot.xstaff_id =
          slot.xstaffs[Math.floor(Math.random() * slot.xstaffs.length)];
      }
      let r = this.reservations.find((r) =>
        r.bookings.find(
          (b) =>
            b.rbservice_id == slot.serviceId &&
            b.site_id == reservedSlot.site_id
        )
      );
      if (!r) {
        r = {
          bookings: [],
          key: null,
          replace: true,
          basket: {},
        };
        this.reservations.push(r);
      }
      r.bookings.push(reservedSlot);
      const newlyAddedBooking = reservedSlot;

      const response = await this.$api.post(
        "/api/resource/reservation/add_resourcebooking",
        r
      );
      if (response.ok) {
        const x = await response.json();
        if (this.releaseIfDetached(r, x.key)) {
          return;
        }
        this.$set(r, "key", x.key);

        const targetBookingIndex = r.bookings.findIndex(
          (b) =>
            b.start === newlyAddedBooking.start &&
            b.staff_id === newlyAddedBooking.staff_id &&
            b.rbservice_id === newlyAddedBooking.rbservice_id
        );

        if (targetBookingIndex !== -1) {
          this.$set(r.bookings[targetBookingIndex], "reserving", false);
        }

        try {
          let ecommerceTrackingItem = {
            id: "r" + reservedSlot.rbservice_id,
            count: reservedSlot.count,
          };

          let service = this.services.find(
            (s) => s.id == reservedSlot.rbservice_id
          );
          if (service) {
            ecommerceTrackingItem.name = service.name;
            ecommerceTrackingItem.price = service.price;
            ecommerceTrackingItem.vat = service.vat;

            this.$ecommercetracking.addToCart(ecommerceTrackingItem);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      } else {
        await this.handleReservationFailure(r, [reservedSlot], response);
      }
    },
    async unreserveSlot(slot) {
      const siteId = slot.sites && slot.sites[0];
      let r = this.reservations.find((r) =>
        r.bookings.find(
          (b) => b.rbservice_id == slot.serviceId && b.site_id == siteId
        )
      );
      if (r) {
        let b = r.bookings.find(
          (b) => b.start == slot.start && b.staff_id == slot.resourceId
        );
        if (b) {
          b.reserving = true;
          if (r.bookings.length == 1) {
            const response = await this.$api.post(
              "/api/resource/reservation/cancel",
              { key: r.key }
            );
            // The server still holds the time if the cancel didn't go through, so it
            // must stay in the list rather than vanish.
            if (!response.ok) {
              await this.handleReservationFailure(r, [], response);
              return;
            }
            this.$set(b, "reserving", false);
            let idx = this.reservations.indexOf(r);
            if (idx != -1) {
              this.reservations.splice(idx, 1);
            }
            this.refetchWeekSlotsIfActive();
          } else {
            const response = await this.$api.post(
              "/api/resource/reservation/add_resourcebooking",
              {
                ...r,
                bookings: r.bookings.filter((i) => i != b),
              }
            );

            if (response.ok) {
              const x = await response.json();
              if (this.releaseIfDetached(r, x.key)) {
                return;
              }
              this.$delete(r.bookings, r.bookings.indexOf(b));
              this.$set(r, "key", x.key);
              this.refetchWeekSlotsIfActive();
            } else {
              // Removal adds nothing — only the reserving flags need clearing.
              await this.handleReservationFailure(r, [], response);
            }
          }
        }
      }
    },
    unreserveAll(wait) {
      let keys = this.reservations.map((r) => r.key);

      if (this.checkoutLoading) {
        return;
      }

      if (keys.length) {
        this.$api
          .post("/api/resource/reservation/cancel", { key: keys })
          .then(() => {
            let unreserve = () => {
              keys.forEach((key) => {
                let idx = this.reservations.findIndex((r) => r.key == key);
                if (idx != -1) {
                  this.reservations.splice(idx, 1);
                }
              });
            };

            if (wait) {
              let unwatch = this.$watch("resourceSlots", unreserve);

              setTimeout(() => {
                if (unwatch) {
                  unwatch();
                } else {
                  unreserve();
                }
              }, 3000);
            } else {
              unreserve();
            }
          });
      }
    },
    // revert undoes what the caller changed before the call — count, optional resources.
    // Returns whether the server accepted the update.
    async updateReservation(r, revert) {
      const touched = r.bookings.slice();
      touched.forEach((b) => this.$set(b, "reserving", true));

      // fetch does not reject on HTTP errors — catch() never ran, leaving them stuck.
      try {
        const response = await this.$api.post(
          "/api/resource/reservation/add_resourcebooking",
          r
        );
        if (!response.ok) {
          if (revert) {
            revert();
          }
          await this.handleReservationFailure(r, [], response);
          return false;
        }
        await response.json();
        return true;
      } catch (error) {
        Sentry.captureException(error);
        return false;
      } finally {
        touched.forEach((b) => this.$set(b, "reserving", false));
      }
    },
    addToCart() {
      const props = [
        "count",
        "rbservice_id",
        "reservation",
        "slots",
        "extra_person",
        "extended_info",
        "tmp_id",
        "site_id",
        "rbooking_id",
        "product_id",
        "variant",
        "price",
        "service",
      ];

      const cartName = "resursbokning";
      this.checkoutItems.forEach((item) => {
        let basketItem = {};
        props.forEach((prop) => {
          if (item[prop] !== undefined) {
            basketItem[prop] = item[prop];
          }
        });
        this.$store.dispatch("addToCart", basketItem);
      });
      this.$store.dispatch("setCartVisibility", {
        cartName,
        showBottomBar: true,
        showCheckout: false,
      });
      this.reservations = [];
      this.showCheckout = false;
    },
    changeNumberOfItems(item, count) {
      if (item.rbooking_id) {
        let index = Number(item.rbooking_id.split("-")[1]) - 1;
        let r = this.reservations[index];
        let b = r.basket[item.product_id];
        let bi = b?.find((bi) => bi.variant === item.variant);
        if (bi) {
          this.$set(bi, "count", count);
        }
      }
    },
    changeItemUsers(item, users) {
      if (item.rbooking_id) {
        let index = Number(item.rbooking_id.split("-")[1]) - 1;
        let r = this.reservations[index];
        let b = r.basket[item.product_id];
        let bi = b?.find((bi) => bi.variant === item.variant);
        if (bi) {
          this.$set(bi, "users", users);
        }
      }
    },
    toggleResource(id) {
      if (this.selectedResourceIds.includes(id)) {
        this.selectedResourceIds = this.selectedResourceIds.filter(
          (slotId) => slotId !== id
        );
      } else {
        this.selectedResourceIds.push(id);
      }
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    async removeBooking(date, startTime, endTime, staffIdToRemove) {
      let bookingToUnreserve = null;
      // Iterate through reservations to find the specific booking
      for (const reservation of this.reservations) {
        for (const booking of reservation.bookings) {
          const bookingDate = booking.start.split(" ")[0];
          const bookingStartTime = booking.start.split(" ")[1];
          const bookingEndTime = booking.end.split(" ")[1];

          if (
            bookingDate === date &&
            bookingStartTime === startTime &&
            bookingEndTime === endTime &&
            booking.staff_id === staffIdToRemove
          ) {
            bookingToUnreserve = booking;
            break;
          }
        }
        if (bookingToUnreserve) break;
      }

      if (bookingToUnreserve) {
        const serviceForBooking = this.services.find(
          (s) => s.id === bookingToUnreserve.rbservice_id
        );
        const slotToUnreserve = {
          start: bookingToUnreserve.start,
          end: bookingToUnreserve.end,
          serviceId: bookingToUnreserve.rbservice_id,
          resourceId: bookingToUnreserve.staff_id,
          sites: [bookingToUnreserve.site_id],
          xstaffs: serviceForBooking ? serviceForBooking.xstaffs : [],
        };

        await this.unreserveSlot(slotToUnreserve);
      }
    },
    async selectSlotColumn(start) {
      this.bulkSelecting = true;
      this.suppressRealtime = true;

      const additions = [];
      for (const resource of this.filteredResources) {
        const slot = resource.slots.find(s => s.start === start);
        if (slot && !this.isSlotSelected(start, resource.resourceId)) {
          additions.push({ resource, slot });
        }
      }
      if (!additions.length) { this.bulkSelecting = false; this.suppressRealtime = false; return; }

      const groups = new Map(); // key: `${serviceId}|${siteId}` -> { serviceId, siteId, toAdd: [] }
      for (const { resource, slot } of additions) {
        const siteId = this.siteIdFor(resource, slot);
        const key = `${resource.serviceId}|${siteId}`;
        if (!groups.has(key)) groups.set(key, { serviceId: resource.serviceId, siteId, toAdd: [] });
        groups.get(key).toAdd.push(this.buildBookingObj(resource, slot));
      }

      try {
        await Promise.all(Array.from(groups.values()).map(async (g) => {
          let r = this.reservations.find(x =>
            x.bookings.length &&
            x.bookings[0].rbservice_id === g.serviceId &&
            x.bookings[0].site_id === g.siteId
          );
          const existing = r ? r.bookings : [];
          const added = g.toAdd.map(b => ({ ...b, reserving: true }));
          const merged = existing.concat(added);
          if (!r) {
            r = { bookings: [], key: null, replace: true, basket: {} };
            this.reservations.push(r);
          }
          this.$set(r, "bookings", merged);

          const resp = await this.$api.post("/api/resource/reservation/add_resourcebooking", r);
          if (!resp.ok) {
            await this.handleReservationFailure(r, added, resp);
            return;
          }
          const x = await resp.json();
          if (this.releaseIfDetached(r, x.key)) return;
          this.$set(r, "key", x.key);
          r.bookings.forEach(b => this.$set(b, "reserving", false));

        }));
      } finally {
        this.bulkSelecting = false;
        this.suppressRealtime = false;
        this.getFreeSlots();
        this.checkAvailabilityForReservedSlots();
      }
    },
    async deselectSlotColumn(start) {
      this.bulkSelecting = true;
      this.suppressRealtime = true;

      try {
        const tasks = [];
        for (const r of [...this.reservations]) {
          const keep = r.bookings.filter(b => b.start !== start);
          if (keep.length === r.bookings.length) continue; 

          if (!keep.length) {
            tasks.push((async () => {
              await this.$api.post("/api/resource/reservation/cancel", { key: r.key });
              const idx = this.reservations.indexOf(r);
              if (idx !== -1) this.reservations.splice(idx, 1);
            })());
          } else {
            tasks.push((async () => {
              const payload = { ...r, bookings: keep.map(b => ({ ...b, reserving: true })) };
              const resp = await this.$api.post("/api/resource/reservation/add_resourcebooking", payload);
              if (!resp.ok) throw new Error("Reservation update failed");
              const x = await resp.json();
              this.$set(r, "key", x.key);
              this.$set(r, "bookings", keep.map(b => ({ ...b, reserving: false })));
            })());
          }
        }
        await Promise.all(tasks);
      } finally {
        this.bulkSelecting = false;
        this.suppressRealtime = false;
        this.getFreeSlots();
        this.checkAvailabilityForReservedSlots();
      }
    },
    onToggleDay(date) {
      if (this.daysWithSelection.has(date)) this.clearDay(date);
      else this.reserveAllForDate(date);
    },
    normalizeViewMode() {
      if (this.viewSetting !== 'both') this.viewMode = this.viewSetting;
      if (!this.weekViewAvailable) this.viewMode = 'day';
    },
    refetchWeekSlotsIfActive() {
      // Removing a booking frees the slot again; without this the week grid keeps
      // showing it as taken until the page reloads.
      if (this.viewMode === 'week') this.fetchWeekSlots(false);
    },
    isWeekGroupBooking(booking) {
      // The week view only owns bookings for its own group and service — the day
      // toggle and per-cell clear must leave everything else in the cart alone.
      if (!this.weekGroupIds.includes(booking.staff_id)) return false;
      const serviceId = this.selectedService && this.selectedService.id;
      return !serviceId || booking.rbservice_id === serviceId;
    },
    async reserveGroupForSlots(slots) {
      // Book every group resource that's free for each given slot, batched per
      // service|site (one add_resourcebooking per group), with optimistic-revert.
      const resources = this.weekGroupResources;
      if (!slots.length || !resources.length) return;
      this.bulkSelecting = true;
      this.suppressRealtime = true;
      try {
        const additions = [];
        for (const slot of slots) {
          for (const resource of resources) {
            if (!slot.staffs.includes(resource.resourceId)) continue;
            if (this.isSlotSelected(slot.start, resource.resourceId)) continue;
            additions.push({ resource, slot });
          }
        }
        if (!additions.length) return;

        const groups = new Map();
        for (const { resource, slot } of additions) {
          const siteId = this.siteIdFor(resource, slot);
          const key = `${resource.serviceId}|${siteId}`;
          if (!groups.has(key)) groups.set(key, { serviceId: resource.serviceId, siteId, toAdd: [] });
          groups.get(key).toAdd.push(this.buildBookingObj(resource, slot));
        }

        await Promise.all(Array.from(groups.values()).map(async (g) => {
          let r = this.reservations.find(x =>
            x.bookings.length &&
            x.bookings[0].rbservice_id === g.serviceId &&
            x.bookings[0].site_id === g.siteId
          );
          const existing = r ? r.bookings : [];
          const added = g.toAdd.map(b => ({ ...b, reserving: true }));
          const merged = existing.concat(added);
          if (!r) {
            r = { bookings: [], key: null, replace: true, basket: {} };
            this.reservations.push(r);
          }
          this.$set(r, "bookings", merged);

          const resp = await this.$api.post("/api/resource/reservation/add_resourcebooking", r);
          if (!resp.ok) {
            await this.handleReservationFailure(r, added, resp);
            return;
          }
          const x = await resp.json();
          if (this.releaseIfDetached(r, x.key)) return;
          this.$set(r, "key", x.key);
          r.bookings.forEach(b => this.$set(b, "reserving", false));
        }));
      } finally {
        this.bulkSelecting = false;
        this.suppressRealtime = false;
        this.getFreeSlots();
        this.fetchWeekSlots(false);
        this.checkAvailabilityForReservedSlots();
      }
    },
    reserveAllForDate(date) {
      // Only what the grid draws as bookable — a slot where just part of the group
      // is free would otherwise land in the cart as a cell the user can't see or undo.
      const slots = this.weekSlots.filter(s => {
        const d = Date.newFull(s.start);
        return d.yyyymmdd() === date && this.weekCellStates[`${date}|${d.hhmm()}`] === 'bookable';
      });
      return this.reserveGroupForSlots(slots);
    },
    onBookSlot({ date, time }) {
      // already selected → un-book (works even after the slot dropped from /get)
      if (this.weekSelectedKeys.has(`${date}|${time}`)) {
        this.clearSlotAt(date, time);
        return;
      }
      const slot = this.weekSlotByKey[`${date} ${time}`];
      if (slot) this.reserveGroupForSlots([slot]);
    },
    async clearSlotAt(date, time) {
      await this.removeBookingsMatching(b => {
        const d = Date.newFull(b.start);
        return d.yyyymmdd() === date && d.hhmm() === time && this.isWeekGroupBooking(b);
      });
    },
    async clearDay(date) {
      await this.removeBookingsMatching(
        b => String(b.start).split(' ')[0] === date && this.isWeekGroupBooking(b)
      );
    },
    async removeBookingsMatching(shouldRemove) {
      // Drops the matching bookings from their reservations, cancelling a
      // reservation outright when nothing is left. Failures surface as a dialog
      // instead of an unhandled rejection, and the refetch resyncs either way.
      this.bulkSelecting = true;
      this.suppressRealtime = true;
      try {
        const tasks = [];
        for (const r of [...this.reservations]) {
          const keep = r.bookings.filter(b => !shouldRemove(b));
          if (keep.length === r.bookings.length) continue;

          if (!keep.length) {
            tasks.push((async () => {
              const resp = await this.$api.post("/api/resource/reservation/cancel", { key: r.key });
              if (!resp.ok) throw new Error("Reservation cancel failed");
              const idx = this.reservations.indexOf(r);
              if (idx !== -1) this.reservations.splice(idx, 1);
            })());
          } else {
            tasks.push((async () => {
              const payload = { ...r, bookings: keep.map(b => ({ ...b, reserving: true })) };
              const resp = await this.$api.post("/api/resource/reservation/add_resourcebooking", payload);
              if (!resp.ok) throw new Error("Reservation update failed");
              const x = await resp.json();
              this.$set(r, "key", x.key);
              this.$set(r, "bookings", keep.map(b => ({ ...b, reserving: false })));
            })());
          }
        }
        await Promise.all(tasks);
      } catch (e) {
        this.$store.commit("showErrorDialog", {
          title: this.$translate("Something went wrong"),
          text: this.$translate("Something went wrong when the booking was to be removed"),
        });
      } finally {
        this.bulkSelecting = false;
        this.suppressRealtime = false;
        this.getFreeSlots();
        this.fetchWeekSlots(false);
        this.checkAvailabilityForReservedSlots();
      }
    },
    checkoutDone() {
      this.reservations = [];
      this.reset();
    },
    reset() {
      this.unreserveAll(false);
      this.showCheckout = false;
      this.almostdone = false;
      this.currentStep = 0;
      this.extraPersons = [];
      this.selectedSlotHasCapacity = null;
      this.bookingId = getRandomId(8);
      this.readyToPay = false;
      this.readyToPayExceptPaymentMethod = false;
      this.checkoutLoading = false;
      this.getFreeSlots();
    },
    pay() {
      if (this.$store.state.settings?.modules?.WebShop) {
        this.$refs.checkout.completePurchase();
      } else {
        this.bookWithoutWebShop();
      }
    },
    async bookWithoutWebShop() {
      this.checkoutLoading = true;

      for (let r of this.reservations) {
        let bookings = r.bookings.map((b) => ({
          start: b.start,
          end: b.end,
          staff: b.staff_id,
          service: b.rbservice_id,
          extendedInfo: Object.fromEntries(
            Object.values(r.questions || {}).map((q) => [q.name, q.value])
          ),
        }));

        let optional_resource = r.bookings.map((b) => ({
          room_id: b.staff_id,
          resources: b.optional_resources,
        }));

        try {
          const additional_products = (this.additionalProducts[r.key] || [])
            .flatMap((product) =>
              (r.basket[product.id] || [])
                .filter((bi) => bi.count > 0)
                .map((bi) => ({
                  id: product.id,
                  count: bi.count,
                  price: product.price ?? 0,
                  ...(bi.variant !== undefined ? { variant: bi.variant } : {}),
                  ...(bi.users !== undefined ? { users: bi.users } : {}),
                }))
            );
          let response = await this.$api.post(
            "/api/memberapi/resourcebooking/add",
            {
              bookings,
              _extra_info: {
                reservation: [r.key],
                extra_person: this.extraPersons,
                additional_products,
              },
              optional_resource,
            }
          );

          if (!response.ok) {
            throw new Error(this.$translate("Booking error"));
          }
        } catch (error) {
          this.$store.commit("showErrorDialog", {
            title: this.$translate("Booking error"),
            text: error.message,
          });
          this.checkoutLoading = false;
          return;
        }
      }

      this.reservations = [];
      this.reset();
      this.$store.commit("showCheckoutDoneDialog", {
        thankYouText: this.$translate("Booking completed"),
      });
      this.checkoutLoading = false;
    },
    reservationTimeout() {
      this.showCheckout = false;
      this.reservations = [];
      this.resetSelections();
    },
    toggleTagFilter(tagId) {
      const i = this.selectedTagIds.indexOf(tagId);
      if (i >= 0) {
        this.selectedTagIds.splice(i, 1);
      } else {
        this.selectedTagIds.push(tagId);
      }
    },
    resetSelections() {
      this.selectedResourceIds = [];
      this.selectedSlotColumns = [];
      this.selectedTagIds = [];
    },
    openDialog(resourceId) {
      var targetResource = this.resources.find(
        (resource) => resource.resourceId === resourceId
      );

      this.infoResourceTitle = targetResource.title;
      this.infoResourceDescription = targetResource.description;
      this.infoResourceImageKey = targetResource.imagekey;
      this.dialogStaffId = resourceId;

      this.isDialogShown = true;
    },
    formatTimeOnly(timeString) {
      return timeString.split(":").slice(0, 2).join(":");
    },
    formatTimePeriodPart(date, period, skipPeriod) {
      date = Date.newFull(date);
      let when = '';
      if (date.isToday()) {
        if (!skipPeriod && period && period.id == 'morning') {
          when = this.$translate('Today') + ', ' + this.$translate(period.short.capitalize()).toLowerCase();
        } else if (!skipPeriod && period) {
          when = this.$translate('In') + ' ' + this.$translate(period.short.capitalize()).toLowerCase();
        } else {
          when = this.$translate('Today');
        }
      } else if (Date.dateEquals(date, Date.tomorrow())) {
        if (period && !skipPeriod) {
          when = this.$translate('Tomorrow') + ' ' + this.$translate(period.short.capitalize()).toLowerCase();
        } else {
          when = this.$translate('Tomorrow');
        }
      }
      if (when) {
        when += ', ';
      }
      return when;
    },
    formatTimeDatePart(date) {
      date = Date.newFull(date);
      let weekday = this.$translate(date.getWeekDayString());
      weekday = weekday.substring(0, 3);
      let lc = (x) => this.$store.state.language == 'sv' ? x.toLowerCase() : x;
      return `${lc(weekday)} ${date.getDate()} ${lc(this.$translate(date.getMonthNameShort()))}`;
    },
    formatTime(date, period) {
      let when = this.formatTimePeriodPart(date, period);
      return `${when}${this.formatTimeDatePart(date)}`;
    },
    onResize() {
      this.isMobile = window.innerWidth < 600;
    },
    mondayOf(yyyymmdd) {
      return Date.newFull(yyyymmdd).monday().yyyymmdd();
    },
    async fetchWeekSlots(showLoading = true) {
      if (!this.selectedService || !this.selectedResourceIds.length) { this.weekSlots = []; return; }
      if (showLoading) this.isWeekLoading = true;
      try {
        const activeSites = this.activeFilterSites;
        const sites = activeSites.length > 0 ? activeSites.join(',') : undefined;
        const raw = await this.$api.get('/api/public/resourcebooking/get', {
          service: this.selectedService.id,
          start: this.selectedWeekStart,
          end: Date.newFull(this.selectedWeekStart).addDays(this.weekDayCount - 1).yyyymmdd(),
          site: sites,
        });
        this.weekSlots = (raw || []).map(s => ({ start: s.start, end: s.end, staffs: s.staffs || [], site_id: s.site_id }));
      } finally {
        if (showLoading) this.isWeekLoading = false;
      }
    },
    scrollToBookingIfHidden() {
      // Only pull the booking section into view when it isn't already visible,
      // so day navigation doesn't force-scroll the page when the user is
      // already looking at it.
      const el = this.$refs.top;
      if (!el || !el.getBoundingClientRect) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const isVisible = rect.bottom > 0 && rect.top < vh;
      if (!isVisible) this.$vuetify.goTo(el);
    },
    decrementWeek() {
      if (this.isWeekAtCurrent) return;
      this.selectedWeekStart = Date.newFull(this.selectedWeekStart).addDays(-7).yyyymmdd();
    },
    incrementWeek() {
      this.selectedWeekStart = Date.newFull(this.selectedWeekStart).addDays(7).yyyymmdd();
    },
    onWeekPicked(date) {
      this.selectedWeekStart = this.mondayOf(date.yyyymmdd());
      this.showWeekPicker = false;
    },
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
  },
  beforeDestroy() {
    if (typeof window === "undefined") return;

    window.removeEventListener("resize", this.onResize, { passive: true });
  },
  created() {
    this.selectedWeekStart = this.mondayOf(Date.today().yyyymmdd());
    this.normalizeViewMode();
    this.unsubscribe = this.$root.$api.subscribe({
      type: "resourceallocation",
      callback: () => {
        if (!this.suppressRealtime) {
          this.getFreeSlots();
          this.checkAvailabilityForReservedSlots();
          if (this.viewMode === 'week') this.fetchWeekSlots(false);
        }
      },
    });
    window.addEventListener("beforeunload", this.unreserveAll);
    this.startReservationTimers();
  },
  destroyed() {
    this.stopReservationTimers();
    this.unreserveAll(false);
    window.removeEventListener("beforeunload", this.unreserveAll);
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
};
</script>

<style scoped>
.slots-container {
  overflow: visible;
}

.slot-grid {
  gap: 8px;
  white-space: nowrap;
  padding: 8px 0 16px 0;
  overflow: auto;
}

.slot-row {
  gap: 8px;
  justify-content: stretch;
}

.timeslots {
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  gap: 8px;
  position: relative;
  justify-content: center;
  align-items: center;
  height: 50px;
}

.timeslot {
  width: 100px;
  height: 52px;
  display: flex;
}

.timeslot-row-select {
  color: #b0b6c2;
  font-size: 12px;
  text-align: center;
}

.selectable-divider {
  height: 2.3rem;
}

.available-slot:hover {
  cursor: pointer;
  background-color: #c5cbd5;
}

.timeslot p {
  padding: 0;
}

.available-slot {
  padding: 8px;
  background-color: #b0b6c2;
  border-radius: 4px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.selectedslot {
  background-color: #4caf50;
}

.selectedslot:hover {
  background-color: #5bc45e;
}

.select-all-slots {
  height: 20px;
}

.timeslot-row-select {
  color: #b0b6c2;
}

.timeslot-row-select:hover {
  cursor: pointer;
  color: #c5cbd5;
}

.non-selectable {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.top-right-btn {
  position: absolute;
  top: 8px;
  right: 8px;
}

.checkout-summary {
  width: 25%;
  position: sticky;
  right: 0;
  top: 120px;
  left: 0;
}

.sticky-column {
  position: sticky;
  top: 120px;
}

.resource-slot {
  width: 250px;
}

.resource-image-small {
  width: 75px;
  height: 52px;
}

.resource-title {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  max-height: 52px;
}

.resource-card-title {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.OptionalResources {
  max-height: clamp(18rem, 60vh, 38rem);
  overflow: auto;
}

@media (max-height: 700px) {
  .OptionalResources {
    max-height: clamp(16rem, 55vh, 30rem);
  }
}

@media (max-width: 600px) {
  .checkout-summary {
    width: 100%;
  }

  .slot-grid {
    overflow: visible;
  }

  .slots-container {
    overflow: auto;
  }

  .timeslot {
    width: 150px;
  }

  .resource-slot {
    width: 150px !important;
    flex: 0 0 150px;
  }

  .resource-image-small {
    width: 150px;
    height: 75px;
  }
}

/* Compact cards */
.z-compact-resources {
  gap: 4px;
}

.z-compact-chip {
  max-width: 260px;
}

.z-compact-name {
  max-width: 160px;
  display: inline-block;
  vertical-align: middle;
}
@media (max-width: 600px) {
  .z-compact-chip { max-width: 100%; }
  .z-compact-name { max-width: 56vw; }
}
</style>
